/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import EmberObject, { computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import EventStatus from '@linc-technologies/linced-common/enums/events/event-status';
import EventType from '@linc-technologies/linced-common/enums/events/event-type';
import PermissionType from '@linc-technologies/linced-common/enums/events/permission-type';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import type CostMapping from '@linc-technologies/linced-common/models/events/cost-mapping';
import type CostMappingOption from '@linc-technologies/linced-common/models/events/cost-mapping-option';
import type EventCount from '@linc-technologies/linced-common/models/events/event-count';
import type OptionLimit from '@linc-technologies/linced-common/models/events/option-limit';
import type Permission from '@linc-technologies/linced-common/models/events/permission';
import type Risk from '@linc-technologies/linced-common/models/events/risk';
import type Template from '@linc-technologies/linced-common/models/events/template';
import type Goal from '@linc-technologies/linced-common/models/goals/goal';
import type Group from '@linc-technologies/linced-common/models/group';
import type Media from '@linc-technologies/linced-common/models/media';
import type Field from '@linc-technologies/linced-common/models/people-schema/field';
import type Person from '@linc-technologies/linced-common/models/person';
import type School from '@linc-technologies/linced-common/models/school';
import moment from 'moment/moment';

// TS-STANDIN(posts/staffnotice, posts/communitynotice): the notice models are
// in linced-app. status is the field that the event reads.
interface EventNotice extends Model {
  status: number;
}

// Proto: events.Event.
export default class Event extends Model {
  declare [Type]: 'events/event';

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @alias('school.id')
  declare school_id: string | null | undefined;

  @attr('string', { defaultValue: '' })
  declare campusId: string;

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  @attr('number', { defaultValue: 0 })
  declare type: EnumValue<typeof EventType> | 0;

  @computed('type')
  get typeImage() {
    if (!this.type || this.type === EventType.CUSTOM) {
      return null;
    }
    return `/assets/images/event-banners/${EventType.getLabel(this.type)!
      .replace(' ', '-')
      .toLowerCase()}.png`;
  }

  @attr('boolean', { defaultValue: false })
  declare internal: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasPermissions: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasOptions: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasRisks: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasCurriculum: boolean;

  @attr('boolean', { defaultValue: false })
  declare hasPayments: boolean;

  @attr('boolean', { defaultValue: false })
  declare requirePayments: boolean;

  @attr('boolean', { defaultValue: false })
  declare requiresApproval: boolean;

  @attr('boolean', { defaultValue: false })
  declare muteStaffNotifications: boolean;

  @attr('string', { defaultValue: '' })
  declare cancellationReason: string;

  setDefaults() {
    const properties = EmberObject.create({
      internal: true,
      hasPermissions: false,
      hasOptions: false,
      hasRisks: false,
      hasCurriculum: false,
      hasPayments: false,
      requirePayments: false,
      requiresApproval: false,
    } as object) as EmberObject & Record<string, unknown>;
    switch (this.type) {
      case EventType.OFFSITE_EXCURSION:
        properties.setProperties({
          internal: false,
          hasPermissions: true,
          hasOptions: true,
          hasRisks: true,
          hasPayments: true,
          requiresApproval: true,
        });
        break;
      case EventType.FUNDRAISING:
      case EventType.SOCIAL:
        properties.setProperties({
          internal: false,
          hasPayments: true,
        });
        break;
      case EventType.SPORTING:
      case EventType.IN_SCHOOL:
      case EventType.CULTURAL:
      case EventType.ASSEMBLY:
        properties.setProperties({
          internal: false,
        });
        break;
      // case EventType.TEACHING_SESSION: // Hidden until we integrate units
      //   properties.setProperties({
      //     hasCurriculum: true,
      //   });
      //   break;
    }
    // Disallow hasPayments if finance isn't enabled
    if (!this.get('school.pupilBillingEnabled')) {
      properties.setProperties({
        hasPayments: false,
      });
    }
    this.setProperties(properties as Record<string, unknown>);
  }

  @belongsTo('person', { async: true, inverse: null })
  declare authorId: AsyncBelongsTo<Person>;

  @alias('authorId')
  declare author: AsyncBelongsTo<Person>;

  @hasMany('group', { async: true, inverse: null })
  declare groupIds: AsyncHasMany<Group>;

  @alias('groupIds')
  declare groups: AsyncHasMany<Group>;

  // groups is an async hasMany -- mapBy on its PromiseManyArray is
  // deprecated (and triggers the "Do not use A()" deprecation via its
  // internal array-observer setup), so walk it with the safe `forEach`.
  @computed('groups.[]')
  get group_ids() {
    const ids: (string | null)[] = [];
    this.groups.forEach((group) => ids.push(group.id));
    return ids;
  }

  @hasMany('person', { async: true, inverse: null })
  declare staffIds: AsyncHasMany<Person>;

  @alias('staffIds')
  declare staff: AsyncHasMany<Person>;

  @computed('staff.[]')
  get staff_ids() {
    const ids: (string | null)[] = [];
    this.staff.forEach((person) => ids.push(person.id));
    return ids;
  }

  @hasMany('person', { async: true, inverse: null })
  declare studentIds: AsyncHasMany<Person>;

  @alias('studentIds')
  declare students: AsyncHasMany<Person>;

  @computed('students.[]')
  get student_ids() {
    const ids: (string | null)[] = [];
    this.students.forEach((person) => ids.push(person.id));
    return ids;
  }

  @hasMany('person', { async: true, inverse: null })
  declare caregiverIds: AsyncHasMany<Person>;

  @alias('caregiverIds')
  declare caregivers: AsyncHasMany<Person>;

  @computed('caregivers.[]')
  get caregiver_ids() {
    const ids: (string | null)[] = [];
    this.caregivers.forEach((person) => ids.push(person.id));
    return ids;
  }

  @attr('number', { defaultValue: 0 })
  declare start: number;

  @attr('number', { defaultValue: 0 })
  declare duration: number;

  @computed('start', 'duration')
  get end() {
    return this.start + this.duration * 60;
  }

  @computed('start')
  get startDate() {
    return moment.unix(this.start).format('YYYY-MM-DD');
  }

  @attr('number', { defaultValue: 0 })
  declare closeAt: number;

  @attr('boolean', { defaultValue: false })
  declare allowCaregiverComment: boolean;

  @attr('number', { defaultValue: 0 })
  declare status: EnumValue<typeof EventStatus>;

  @attr('string', { defaultValue: '' })
  declare title: string;

  @attr('string', { defaultValue: '' })
  declare description: string;

  @belongsTo('schools/location', { async: true, inverse: null })
  // TS-STANDIN(schools/location): the location model is in linced-app.
  declare locationId: AsyncBelongsTo<Model>;

  @alias('locationId')
  declare locationModel: AsyncBelongsTo<Model>;

  @attr('string', { defaultValue: '' })
  declare location: string;

  @attr('string', { defaultValue: '' })
  declare transport: string;

  @computed('locationModel.name', 'location')
  get locationString(): string | undefined {
    return (
      (this.locationModel.get('name') as string | undefined) || this.location
    );
  }

  @computed('id')
  get url() {
    return `/home/events/${this.id}`;
  }

  // Optional linkage to a Goal (Topic)
  @belongsTo('goals/goal', { async: true, inverse: null })
  declare topicId: AsyncBelongsTo<Goal>;

  @alias('topicId')
  declare topic: AsyncBelongsTo<Goal>;

  @hasMany('posts/staffnotice', { async: true, inverse: 'eventId' })
  declare staffNotices: AsyncHasMany<EventNotice>;

  @hasMany('posts/communitynotice', { async: true, inverse: 'eventId' })
  declare communityNotices: AsyncHasMany<EventNotice>;

  @computed('communityNotices.[]')
  get publishedCommunityNotices() {
    const published: EventNotice[] = [];
    this.communityNotices.forEach((notice) => {
      if (notice.status === PostStatus.PUBLISHED) {
        published.push(notice);
      }
    });
    return published;
  }

  @computed('communityNotices.[]')
  get unpublishedCommunityNotices() {
    const unpublished: EventNotice[] = [];
    this.communityNotices.forEach((notice) => {
      if (notice.status !== PostStatus.PUBLISHED) {
        unpublished.push(notice);
      }
    });
    return unpublished;
  }

  @hasMany('events/permission', { async: true, inverse: null })
  declare permissionIds: AsyncHasMany<Permission>;

  @alias('permissionIds')
  declare permissions: AsyncHasMany<Permission>;

  @computed('permissions.[]')
  get permission_ids() {
    const ids: (string | null)[] = [];
    this.permissions.forEach((permission) => ids.push(permission.id));
    return ids;
  }

  @hasMany('people-schema/field', { async: true, inverse: null })
  declare permissionFieldIds: AsyncHasMany<Field>;

  @alias('permissionFieldIds')
  declare permissionFields: AsyncHasMany<Field>;

  @computed('permissionFields.[]')
  get permissionField_ids() {
    const ids: (string | null)[] = [];
    this.permissionFields.forEach((field) => ids.push(field.id));
    return ids;
  }

  @computed('permissionFields.[]')
  get permissionField_keys() {
    const keys: (string | undefined)[] = [];
    this.permissionFields.forEach((field) => keys.push(field.key));
    return keys;
  }

  @computed('permissions.[]', 'permissionFields.[]')
  get allPermissions() {
    const all: unknown[] = [];
    this.permissions.forEach((permission) => all.push(permission));
    this.permissionFields.forEach((field) => all.push(field));
    return all;
  }

  @computed('permissions.[]')
  get permissionPermissions() {
    const result: Permission[] = [];
    this.permissions.forEach((permission) => {
      if (permission.type === PermissionType.PERMISSION) {
        result.push(permission);
      }
    });
    return result;
  }

  @computed('permissions.[]')
  get optionPermissions() {
    const result: Permission[] = [];
    this.permissions.forEach((permission) => {
      if (permission.type === PermissionType.OPTION) {
        result.push(permission);
      }
    });
    return result;
  }

  @hasMany('events/permission', { async: true, inverse: null })
  declare mandatoryPermissionIds: AsyncHasMany<Permission>;

  @alias('mandatoryPermissionIds')
  declare mandatoryPermissions: AsyncHasMany<Permission>;

  @computed('mandatoryPermissions.[]')
  get mandatory_permission_ids() {
    const ids: (string | null)[] = [];
    this.mandatoryPermissions.forEach((permission) => ids.push(permission.id));
    return ids;
  }

  @hasMany('people-schema/field', { async: true, inverse: null })
  declare mandatoryPermissionFieldIds: AsyncHasMany<Field>;

  @alias('mandatoryPermissionFieldIds')
  declare mandatoryPermissionFields: AsyncHasMany<Field>;

  @computed('mandatoryPermissionFields.[]')
  get mandatory_permission_field_ids() {
    const ids: (string | null)[] = [];
    this.mandatoryPermissionFields.forEach((field) => ids.push(field.id));
    return ids;
  }

  // Cost-incurring options subset
  @hasMany('events/permission', { async: true, inverse: null })
  declare costIncurringPermissionIds: AsyncHasMany<Permission>;

  @alias('costIncurringPermissionIds')
  declare costIncurringPermissions: AsyncHasMany<Permission>;

  @computed('costIncurringPermissions.[]')
  get cost_incurring_permission_ids() {
    const ids: (string | null)[] = [];
    this.costIncurringPermissions.forEach((permission) =>
      ids.push(permission.id),
    );
    return ids;
  }

  // Curriculum and notes content
  @attr('string', { defaultValue: '' })
  declare curriculum: string;

  @attr('string', { defaultValue: '' })
  declare notes: string;

  @attr('string', { defaultValue: '' })
  declare eventImageId: string;

  @alias('eventImageId')
  declare eventImage: string | undefined;

  // Template linkage
  @belongsTo('events/template', { async: true, inverse: null })
  declare templateId: AsyncBelongsTo<Template>;

  @alias('templateId')
  declare template: AsyncBelongsTo<Template>;

  @fragmentArray('events/cost-mapping-option')
  declare intentionToAttendCostMappingOptions: FragmentArray<CostMappingOption>;

  @fragmentArray('events/cost-mapping')
  declare optionCostMappings: FragmentArray<CostMapping>;

  @fragmentArray('events/option-limit')
  declare optionLimits: FragmentArray<OptionLimit>;

  @attr('number', { defaultValue: 0 })
  declare maxAttendees: number;

  // We should actually only have one of these
  @hasMany('events/event-count', { async: true, inverse: 'eventId' })
  declare eventCounts: AsyncHasMany<EventCount>;

  @computed('eventCounts.[]')
  get eventCount() {
    // eventCounts.at() doesn't exist on the async hasMany's PromiseManyArray
    // -- length and forEach are the only safe accessors, so collect first.
    const eventCounts: EventCount[] = [];
    this.eventCounts.forEach((eventCount) => eventCounts.push(eventCount));
    return eventCounts.length ? eventCounts[0] : null;
  }

  @computed('maxAttendees', 'eventCount.currentAttendees')
  get isFull() {
    if (!this.eventCount || !this.maxAttendees) {
      return false;
    }
    return this.eventCount.currentAttendees >= this.maxAttendees;
  }

  // Risk analysis
  @hasMany('events/risk', { async: true, inverse: null })
  declare riskIds: AsyncHasMany<Risk>;

  @alias('riskIds')
  declare risks: AsyncHasMany<Risk>;

  @computed('risks.[]')
  get risk_ids() {
    const ids: (string | null)[] = [];
    this.risks.forEach((risk) => ids.push(risk.id));
    return ids;
  }

  @attr('string', { defaultValue: '' })
  declare riskMitigations: string;

  @hasMany('media', { async: true, inverse: null })
  declare riskMediaIds: AsyncHasMany<Media>;

  @alias('riskMediaIds')
  declare riskMedia: AsyncHasMany<Media>;

  @computed('riskMedia.[]')
  get riskMedia_ids() {
    const ids: (string | null)[] = [];
    this.riskMedia.forEach((media) => ids.push(media.id));
    return ids;
  }

  @attr('boolean', { defaultValue: false })
  declare riskAcknowledgement: boolean;

  @computed('status')
  get isClosed() {
    return this.status === EventStatus.CLOSED;
  }

  @computed('status')
  get calendarClass() {
    switch (this.status) {
      case EventStatus.DRAFT:
        return 'event-draft';
      case EventStatus.PENDING_APPROVAL:
        return 'event-pending';
      case EventStatus.APPROVED:
        return 'event-approved';
      case EventStatus.OPEN_TO_CAREGIVERS:
      case EventStatus.OPEN_TO_STAFF:
        return 'event-public';
      case EventStatus.COMPLETE:
        return 'event-complete';
      case EventStatus.CANCELLED:
        return 'event-cancelled';
      case EventStatus.CLOSED:
        return 'event-closed';
    }
  }

  @computed('id', 'title', 'locationString', 'start', 'end', 'calendarClass')
  get fullCalendarEventModel() {
    return {
      id: this.id,
      title: this.title + ' - ' + this.locationString,
      start: moment.unix(this.start).format(),
      end: moment.unix(this.end).format(),
      className: this.calendarClass,
    };
  }

  @computed('description')
  get descriptionInvalid() {
    const description = this.description;
    return !description || description === '<p></p>';
  }

  @computed('groups.[]', 'title', 'duration', 'locationModel.id', 'location')
  get definitionInvalid() {
    if (this.groups.length === 0) {
      return true;
    }
    if (!this.title) {
      return true;
    }
    if (this.duration < 1) {
      return true;
    }
    return isEmpty(this.locationModel.get('id')) && !this.location;
  }

  @computed('closeAt', 'start', 'status')
  get closeAtInvalid() {
    if (this.status >= EventStatus.CLOSED) {
      return false;
    }
    // 0 means no automatic closure.
    if (!this.closeAt) {
      return false;
    }
    const now = moment().unix();
    return this.closeAt < now || this.closeAt > this.start;
  }

  get financeIncomplete() {
    if (!this.hasPayments) {
      return false;
    }

    if (
      this.intentionToAttendCostMappingOptions
        .toArray()
        .some(
          (intentionToAttendCostMappingOption) =>
            intentionToAttendCostMappingOption.amount &&
            !intentionToAttendCostMappingOption.ledgerItemId,
        )
    ) {
      return true;
    }

    // `costIncurringPermissions` is an async hasMany, so reading it yields a
    // PromiseManyArray. Unlike `this.hasMany(...).value()` it triggers the load
    // and is autotracked, but it only proxies `length`, `[]` and `forEach` to
    // the underlying ManyArray. `forEach` consumes the membership tag, so this
    // getter stays reactive while the logic below works on a native array.
    const costIncurringPermissions: Permission[] = [];
    this.costIncurringPermissions.forEach((costIncurringPermission) =>
      costIncurringPermissions.push(costIncurringPermission),
    );

    const optionCostMappings = this.optionCostMappings.toArray();

    return costIncurringPermissions.some((costIncurringPermission) => {
      const optionCostMapping = optionCostMappings.find(
        (optionCostMapping) =>
          optionCostMapping.optionId === costIncurringPermission.id,
      );
      // Treat missing mappings as complete
      if (!optionCostMapping) {
        return false;
      }

      return optionCostMapping.costMappingOptions
        .toArray()
        .some(
          (costMappingOption) =>
            costMappingOption.amount && !costMappingOption.ledgerItemId,
        );
    });
  }
}
